import { Capacitor } from '@capacitor/core';
import { TRACKING_NOTIFICATION_TITLE } from '@/constants/honest-ui';

const TRACKING_NOTIFICATION_ID = 1001;

class LocalNotificationService {
  private permissionGranted = false;

  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const perm = await LocalNotifications.checkPermissions();
      this.permissionGranted = perm.display === 'granted';

      if (!this.permissionGranted) {
        const req = await LocalNotifications.requestPermissions();
        this.permissionGranted = req.display === 'granted';
      }
    } catch {
      // Plugin not available
    }
  }

  async notifySyncComplete(synced: number, failed: number): Promise<void> {
    if (!Capacitor.isNativePlatform() || !this.permissionGranted) return;

    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const body =
        failed > 0
          ? `${synced} synced, ${failed} failed`
          : `${synced} item${synced !== 1 ? 's' : ''} synced`;

      await LocalNotifications.schedule({
        notifications: [
          {
            title: 'Sync Complete',
            body,
            id: Date.now() % 2147483647, // Android requires 32-bit int
          },
        ],
      });
    } catch {
      // Ignore errors
    }
  }

  async notifyTrackingActive(message: string): Promise<void> {
    if (!Capacitor.isNativePlatform() || !this.permissionGranted) return;

    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');

      // ongoing+autoCancel:false makes the notification non-dismissible on
      // Android so the responder can see a session is open. It is a plain
      // local notification, not a location foreground service, so it does not
      // keep the watcher alive in the background: tracking is foreground-only
      // (P0-12), and the title/body say so instead of "GPS Tracking Active".
      await LocalNotifications.schedule({
        notifications: [
          {
            title: TRACKING_NOTIFICATION_TITLE,
            body: message,
            id: TRACKING_NOTIFICATION_ID,
            ongoing: true,
            autoCancel: false,
          },
        ],
      });
    } catch {
      // Ignore errors
    }
  }

  async clearTrackingNotification(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      await LocalNotifications.cancel({
        notifications: [{ id: TRACKING_NOTIFICATION_ID }],
      });
    } catch {
      // Ignore errors
    }
  }
}

export const localNotificationService = new LocalNotificationService();